import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import './ApplicationForm.css';

const ApplicationForm = ({ applicantId, onComplete }) => {
    const [questions, setQuestions] = useState([]);
    const [sections, setSections] = useState({});
    const [sectionOrder, setSectionOrder] = useState([]);
    const [responses, setResponses] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [animating, setAnimating] = useState(false);

    useEffect(() => {
        loadQuestions();
        loadExistingResponses();
    }, [applicantId]);

    const loadQuestions = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/application/questions`);
            if (response.ok) {
                const data = await response.json();
                const questionsList = data.questions || [];
                const sectionsData = data.sections || {};
                
                setQuestions(questionsList);
                setSections(sectionsData);
                
                // Create ordered section list
                const orderedSections = Object.keys(sectionsData).sort((a, b) => {
                    const aFirst = sectionsData[a][0]?.order_index || 0;
                    const bFirst = sectionsData[b][0]?.order_index || 0;
                    return aFirst - bFirst;
                });
                setSectionOrder(orderedSections);
                
                if (orderedSections.length > 0) {
                    setCurrentStep(0);
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.error || 'Failed to load application form');
            }
        } catch (err) {
            console.error('Error loading questions:', err);
            setError('Failed to load application form. Please refresh the page.');
        } finally {
            setLoading(false);
        }
    };

    const loadExistingResponses = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/application`);
            if (response.ok) {
                const data = await response.json();
                if (data.responses) {
                    const responsesObj = {};
                    data.responses.forEach(r => {
                        responsesObj[r.field_name] = r.response_value;
                    });
                    setResponses(responsesObj);
                }
            }
        } catch (err) {
            console.error('Error loading responses:', err);
        }
    };

    const handleChange = (fieldName, value) => {
        setResponses(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const handleNext = () => {
        if (currentStep < sectionOrder.length - 1) {
            setAnimating(true);
            setTimeout(() => {
                setCurrentStep(prev => prev + 1);
                setAnimating(false);
            }, 300);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setAnimating(true);
            setTimeout(() => {
                setCurrentStep(prev => prev - 1);
                setAnimating(false);
            }, 300);
        }
    };

    const handleSave = async (isSubmit = false) => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/application`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    responses,
                    application_completed: isSubmit ? 1 : 0
                })
            });

            if (response.ok) {
                if (isSubmit) {
                    setSuccess('Application submitted successfully!');
                    setTimeout(() => {
                        if (onComplete) {
                            onComplete();
                        }
                    }, 2000);
                } else {
                    setSuccess('Progress saved!');
                    setTimeout(() => setSuccess(''), 3000);
                }
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to save application');
            }
        } catch (err) {
            console.error('Error saving application:', err);
            setError('Failed to save application');
        } finally {
            setSaving(false);
        }
    };

    const renderField = (question) => {
        const { field_name, field_type, required, placeholder, options } = question;
        const value = responses[field_name] || '';

        switch (field_type) {
            case 'text':
            case 'email':
            case 'tel':
            case 'date':
                return (
                    <input
                        type={field_type}
                        className="form-input"
                        value={value}
                        onChange={(e) => handleChange(field_name, e.target.value)}
                        placeholder={placeholder || ''}
                        required={required}
                    />
                );
            
            case 'number':
                return (
                    <input
                        type="number"
                        className="form-input"
                        value={value}
                        onChange={(e) => handleChange(field_name, e.target.value)}
                        placeholder={placeholder || ''}
                        required={required}
                    />
                );
            
            case 'textarea':
                return (
                    <textarea
                        className="form-textarea"
                        value={value}
                        onChange={(e) => handleChange(field_name, e.target.value)}
                        placeholder={placeholder || ''}
                        required={required}
                        rows={4}
                    />
                );
            
            case 'select':
                let optionList = [];
                if (options) {
                    if (typeof options === 'string') {
                        try {
                            optionList = JSON.parse(options);
                        } catch {
                            optionList = options.split(',').map(o => o.trim());
                        }
                    } else if (Array.isArray(options)) {
                        optionList = options;
                    }
                }
                return (
                    <select
                        className="form-select"
                        value={value}
                        onChange={(e) => handleChange(field_name, e.target.value)}
                        required={required}
                    >
                        <option value="">Select an option...</option>
                        {optionList.map((opt, idx) => (
                            <option key={idx} value={opt}>{opt}</option>
                        ))}
                    </select>
                );
            
            case 'checkbox':
                return (
                    <label className="form-checkbox">
                        <input
                            type="checkbox"
                            checked={value === 'true' || value === true || value === '1'}
                            onChange={(e) => handleChange(field_name, e.target.checked ? '1' : '0')}
                            required={required}
                        />
                        <span>I agree to the above statement</span>
                    </label>
                );
            
            default:
                return (
                    <input
                        type="text"
                        className="form-input"
                        value={value}
                        onChange={(e) => handleChange(field_name, e.target.value)}
                        placeholder={placeholder || ''}
                        required={required}
                    />
                );
        }
    };

    if (loading) {
        return (
            <div className="application-form-loading">
                <div className="loading-spinner"></div>
                <p>Loading application form...</p>
            </div>
        );
    }

    if (sectionOrder.length === 0) {
        return (
            <div className="application-form-error">
                <p>No application questions found. Please contact support.</p>
            </div>
        );
    }

    const currentSectionName = sectionOrder[currentStep];
    const currentSectionQuestions = sections[currentSectionName] || [];
    const progress = ((currentStep + 1) / sectionOrder.length) * 100;

    return (
        <div className="application-form-slideshow">
            {error && (
                <div className="form-alert form-alert-error">
                    {error}
                    <button onClick={() => setError('')}>×</button>
                </div>
            )}
            {success && (
                <div className="form-alert form-alert-success">
                    {success}
                    <button onClick={() => setSuccess('')}>×</button>
                </div>
            )}

            {/* Progress Bar */}
            <div className="slideshow-progress">
                <div className="progress-bar">
                    <div 
                        className="progress-fill" 
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
                <div className="progress-text">
                    Step {currentStep + 1} of {sectionOrder.length}
                </div>
            </div>

            {/* Section Indicator */}
            <div className="section-indicator">
                {sectionOrder.map((sectionName, idx) => (
                    <div
                        key={sectionName}
                        className={`section-dot ${idx === currentStep ? 'active' : idx < currentStep ? 'completed' : ''}`}
                        onClick={() => {
                            if (idx <= currentStep || idx === currentStep + 1) {
                                setAnimating(true);
                                setTimeout(() => {
                                    setCurrentStep(idx);
                                    setAnimating(false);
                                }, 300);
                            }
                        }}
                        title={sectionName}
                    >
                        {idx < currentStep && '✓'}
                    </div>
                ))}
            </div>

            {/* Slide Container */}
            <div className={`slideshow-container ${animating ? 'animating' : ''}`}>
                <div 
                    className="slide-wrapper"
                    style={{ transform: `translateX(-${currentStep * 100}%)` }}
                >
                    {sectionOrder.map((sectionName, sectionIdx) => (
                        <div key={sectionName} className="slide">
                            <div className="slide-content">
                                <h2 className="slide-title">{sectionName}</h2>
                                <p className="slide-subtitle">
                                    Please provide the following information
                                </p>
                                
                                <div className="slide-fields">
                                    {sections[sectionName].map((question) => (
                                        <div key={question.id} className="form-field">
                                            <label className="form-label">
                                                {question.question_text}
                                                {question.required && <span className="required">*</span>}
                                            </label>
                                            {renderField(question)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="slideshow-navigation">
                <button
                    className="nav-button nav-button-prev"
                    onClick={handlePrevious}
                    disabled={currentStep === 0 || saving}
                >
                    ← Previous
                </button>
                
                <button
                    className="nav-button nav-button-save"
                    onClick={() => handleSave(false)}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Progress'}
                </button>
                
                {currentStep < sectionOrder.length - 1 ? (
                    <button
                        className="nav-button nav-button-next"
                        onClick={handleNext}
                        disabled={saving}
                    >
                        Next →
                    </button>
                ) : (
                    <button
                        className="nav-button nav-button-submit"
                        onClick={() => handleSave(true)}
                        disabled={saving}
                    >
                        {saving ? 'Submitting...' : 'Submit Application'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default ApplicationForm;
